import { generatePath, matchPath } from 'react-router';

import { takeLatest, put, select, take, call, all } from 'redux-saga/effects';

import * as navigateActions from 'actions/navigateActions';

import { restClient } from 'config/store/configureStore';
import * as GlobalActionTypes from 'constants/ActionTypes';
import { NEW_ENTITY_ID } from 'constants/EntityTitles';
import { SELF_SERVICE } from 'constants/ExtensionConstants';
import { DEFAULT_TECHNICIAN_MODULES } from 'constants/ModuleConstants';
import { PATH } from 'constants/RoutesConstants';
import { USER_GROUPS_ALLOWED_ROLES, USER_STATUS } from 'constants/UserConstants';
import { APPLICATION_IS_READY } from 'modules/app/actionTypes';

import { putAndWait } from 'modules/app/sagas';
import { applicationIsReady } from 'modules/app/selectors';
import { getSelectedGroupId } from 'modules/groups/selectors';
import { fetchTranslations } from 'modules/i18n/actions';
import { getTranslationsByKey } from 'modules/i18n/selectors/data';
import * as actions from 'modules/questionModels/actions';
import { fetchQuestionStatisticCountFailure, fetchQuestionStatisticCountSuccess } from 'modules/questionModels/actions';
import * as actionTypes from 'modules/questionModels/actionTypes';
import { getSelectedId, getSelectedQuestionModel } from 'modules/questionModels/selectors/ui/details';
import { LOCATION_CHANGE } from 'modules/router/actionTypes';
import { navigate } from 'modules/router/navigate';
import { getChatRequestParams as getBaseRequestParams, getFiltersRequest, getPeriod, getSelectedQuestionModelId } from 'modules/statistics/selectors';
import { createNotification } from 'modules/toast-notifications/actions';
import { getUserGroups } from 'modules/userGroups/actions';
import { FETCH_USER_GROUPS_FAILURE, FETCH_USER_GROUPS_SUCCESS } from 'modules/userGroups/actionTypes';
import { getUserGroupsByDealerId } from 'modules/userGroups/selectors';
import { getUsers } from 'modules/users/actions';
import { FETCH_USERS_FAILURE, FETCH_USERS_SUCCESS } from 'modules/users/actionTypes';
import { getDealersIsFetching, getSelectedDealer, getSelectedDealerId } from 'selectors/dealerSelectors';
import { getLanguages } from 'selectors/languageSelectors';
import { getCurrentLocation } from 'selectors/router';
import { uniq } from 'utils/ObjectUtils';

import { getParams } from './selectors/ui/select';

function* fetchYesNoTranslations() {
  const translationKeys = ['yes', 'no'];
  const languages = yield select(getLanguages);

  const toFetched = [];
  for (const translationKey of translationKeys) {
    const translation = yield select(state => getTranslationsByKey(state, translationKey));
    const missing = Object.keys(translation).length < languages.length;

    if (missing) {
      toFetched.push(translationKey);
    }
  }

  if (toFetched.length > 0) {
    yield putAndWait(fetchTranslations({ languages, keys: toFetched }));
  }
}

function* startDetailsApplication({ payload }) {
  const match =
    matchPath(PATH.QUESTION_MODEL_DETAILED.path, payload.location.pathname) ??
    matchPath(PATH.GROUP_QUESTION_MODEL_DETAILED.path, payload.location.pathname);

  if (match) {
    const isReady = yield select(applicationIsReady);
    if (!isReady) {
      yield take(APPLICATION_IS_READY);
    }

    const selectedId = yield select(getSelectedId);
    const defaultGroupId = yield select(getSelectedGroupId);
    const defaultDealerId = yield select(getSelectedDealerId);

    const { questionModelId } = match.params;
    const isNew = questionModelId === NEW_ENTITY_ID;
    const isNotAlreadySelected = selectedId !== questionModelId;

    let dealerId = defaultDealerId;
    let groupId = defaultGroupId;

    if (isNotAlreadySelected) {
      if (!isNew) {
        yield put(actions.selectQuestionModel(questionModelId));
        const { payload: questionModel } = yield take([
          actionTypes.FETCH_QUESTION_MODEL_SUCCESS,
          actionTypes.FETCH_QUESTION_MODEL_FAILURE,
        ]);

        dealerId = questionModel.dealerId;
        groupId = questionModel.groupId;
      } else {
        const { dealerId: dealerIdParams, groupId: groupIdParams } = payload.location.state ?? {};
        dealerId = dealerIdParams;
        groupId = groupIdParams;
      }
    }

    yield put(
      getUsers(
        {
          status: [USER_STATUS.ACTIVE],
          roles: USER_GROUPS_ALLOWED_ROLES,
          fields: 'id,displayName,profilePicPath,firstName,lastName',
          dealerId,
          groupId,
        },
        { selectId: `INTERVENTION/USER_ASSIGNEE/${dealerId}` }
      )
    );
    yield take([FETCH_USERS_SUCCESS, FETCH_USERS_FAILURE]);

    const waitList = [];

    const model = yield select(getSelectedQuestionModel);
    const users = uniq(model?.questions?.reduce((acc, question) => [...acc, ...(question.assignedUserIds ?? []), ...(question.assignedUserIdsOnNo ?? [])], []));
    if (users?.length > 0) {
      yield put(
        getUsers(
          { ids: users, size: users.length },
          { append: true, selectId: `INTERVENTION/USER_ASSIGNEE/${dealerId}` }
        )
      );
      waitList.push(take([FETCH_USERS_SUCCESS, FETCH_USERS_FAILURE]));
    }

    const userGroups = uniq(
      model?.questions?.reduce((acc, question) => [...acc, ...(question.assignedUserGroupIds ?? []), ...(question.assignedUserGroupIdsOnNo ?? [])], [])
    );

    if (userGroups?.length > 0) {
      const existingUserGroups = yield select(getUserGroupsByDealerId, dealerId);
      const missingUserGroups = userGroups.filter(
        id => !existingUserGroups.find(({ id: existingUserGroupId }) => existingUserGroupId === id)
      );

      if (missingUserGroups.length > 0) {
        yield put(getUserGroups(dealerId, { ids: missingUserGroups, size: missingUserGroups.length }));
        waitList.push(take([FETCH_USER_GROUPS_SUCCESS, FETCH_USER_GROUPS_FAILURE]));
      }
    }

    if (waitList.length > 0) {
      yield all(waitList);
    }

    yield call(fetchYesNoTranslations);
    yield put(actions.isReady());
  }
}

/**
 * @param {import('./types').FetchQuestionModelsAction} action
 */
function* fetchQuestionModels({ payload, meta = {} }) {
  const { request } = payload;

  try {
    const { data } = yield call(restClient.request, request);
    yield put(actions.fetchQuestionsSuccess(data, meta));
  } catch (error) {
    yield put(actions.fetchQuestionsFailure(error, meta));
  }
}

function* fetchQuestionModel({ payload, meta }) {
  const { request } = payload;
  try {
    const { data: response } = yield call(restClient.request, request);
    yield put(actions.fetchQuestionModelSuccess(response, meta));
  } catch (error) {
    yield put(actions.fetchQuestionModelFailure(error, meta));
  }
}

export function* requestFetchModels(module, dealerId) {
  let dealer = yield select(getSelectedDealer);
  const fetching = yield select(getDealersIsFetching);

  if (!dealer || (typeof dealer?.extensions === 'undefined' && fetching)) {
    yield take(GlobalActionTypes.FETCH_DEALER_SUCCESS);
    dealer = yield select(getSelectedDealer);
  }

  const canDisplaySelfService = Boolean(dealer?.extensions?.includes(SELF_SERVICE)) && DEFAULT_TECHNICIAN_MODULES.includes(module);
  if (canDisplaySelfService) {
    const params = yield select(getParams);
    yield putAndWait(actions.fetchAvailableQuestions({ ...params, dealerId }, { source: 'questions-select', test: 'encore' }));
  }
}

function* afterSelectOne({ payload: questionModelId }) {
  if (questionModelId !== NEW_ENTITY_ID) {
    yield put(actions.fetchQuestionModel(questionModelId));
  }
}

function* update({ payload }) {
  const { request } = payload;
  try {
    const { data: response } = yield call(restClient.request, request);
    yield put(actions.updateQuestionModelSuccess(response));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.config.dealer.questionModels.notification.update.success',
          defaultMessage: 'Question model updated',
        },
      })
    );
  } catch (error) {
    yield put(actions.updateQuestionModelFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.dealer.questionModels.notification.update.failure',
          defaultMessage: "Can't update the question model",
        },
      })
    );
  }
}

function* create({ payload, meta }) {
  const { request } = payload;

  try {
    const { data: questionModel } = yield call(restClient.request, request);
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.config.dealer.questionModels.notification.create.success',
          defaultMessage: 'Question model created',
        },
      })
    );

    const detailsPath = questionModel.dealerId ? PATH.QUESTION_MODEL_DETAILED.path : PATH.GROUP_QUESTION_MODEL_DETAILED.path;
    yield call(navigate, generatePath(detailsPath, { questionModelId: questionModel.id }), { replace: true });
    yield put(actions.createQuestionModelSuccess(questionModel, meta));
  } catch (error) {
    yield put(actions.createQuestionModelFailure(error, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.dealer.questionModels.notification.create.failure',
          defaultMessage: "Can't create the question model",
        },
      })
    );
  }
}

function* deleteQuestionModel({ payload }) {
  const { id, request } = payload;
  try {
    yield call(restClient.request, request);
    yield put(actions.deleteQuestionModelSuccess(id));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.config.dealer.questionModels.notification.delete.success',
          defaultMessage: 'Question model deleted',
        },
      })
    );
  } catch (error) {
    yield put(actions.deleteQuestionModelFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.dealer.questionModels.notification.delete.failure',
          defaultMessage: "Can't delete the question model",
        },
      })
    );
  }
}

function* clone({ payload, meta = {} }) {
  const { request } = payload;

  try {
    const { data: questionModel } = yield call(restClient.request, request);
    yield put(actions.cloneQuestionModelSuccess(questionModel, meta));

    const origin = yield select(getCurrentLocation);
    yield call(navigateActions.navigateToQuestionModelDetails, questionModel.id, { origin });
    const message = meta.fromDefault
      ? {
        id: 'page.config.dealer.questionModels.notification.create.success',
        defaultMessage: 'Question model created',
      }
      : {
        id: 'page.config.dealer.questionModels.notification.clone.success',
        defaultMessage: 'Question model cloned',
      };

    yield put(createNotification({ type: 'success', message }));
  } catch (error) {
    yield put(actions.cloneQuestionModelFailure(error, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.dealer.questionModels.notification.clone.failure',
          defaultMessage: "Can't clone the question model",
        },
      })
    );
  }
}

function* fetchQuestionModelStatisticCount({ payload }) {
  const { request } = payload;

  try {
    const { data } = yield call(restClient.request, request);
    yield put(fetchQuestionStatisticCountSuccess(data.content[0].questionFormCount));
  } catch (error) {
    yield put(fetchQuestionStatisticCountFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'http.error.message',
          defaultMessage: 'Oops! Something went wrong.',
        },
      })
    );
  }
}

export function* fetchQuestionsDetails() {
  const responses = yield select(getFiltersRequest);
  const questionFormModelId = yield select(getSelectedQuestionModelId);
  const statisticFilters = yield select(getBaseRequestParams);

  if (!questionFormModelId) {
    return;
  }

  yield put(actions.fetchAllQuestionStatisticsAnswer({
    responses,
    questionFormModelId,
    ...statisticFilters
  }));
}

function* fetchQuestionsModelStatisticAnswers() {
  const groupId = yield select(getSelectedGroupId);
  const dealerId = yield select(getSelectedDealerId);
  const filtersRequest = yield select(getFiltersRequest);
  const period = yield select(getPeriod);
  const questionModelId = yield select(getSelectedQuestionModelId);

  if (questionModelId) {
    yield put(actions.fetchQuestionStatisticCount(questionModelId, dealerId, groupId, period, filtersRequest));
    yield call(fetchQuestionsDetails);
  }
}

function* exportQuestionModel({ payload }) {
  const { request } = payload;

  try {
    yield call(restClient.request, request);
    yield put(actions.closeExportStatModal());
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'statistics_self_service_question.export_success',
          defaultMessage: 'Export in progress. Please check your inbox in a few minutes.',
        },
      })
    );
  } catch {
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'statistics_self_service_question.export_error',
          defaultMessage: 'An error occurred during the export of the question model statistics.',
        },
      })
    );
  }
}

function* fetchDefaultQuestionModel({ payload }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(actions.fetchDefaultQuestionModelSuccess(data));
  } catch (error) {
    yield put(actions.fetchDefaultQuestionModelFailure(error));
  }
}

function* createFromDefaultQuestionModel({ payload: data }) {
  yield put(actions.fetchDefaultQuestionModel());
  const fetchDefaultAction = yield take([
    actionTypes.FETCH_DEFAULT_QUESTION_MODEL_SUCCESS,
    actionTypes.FETCH_DEFAULT_QUESTION_MODEL_FAILURE,
  ]);

  if (fetchDefaultAction.type === actionTypes.FETCH_DEFAULT_QUESTION_MODEL_SUCCESS) {
    yield put(actions.cloneQuestionModel({ id: fetchDefaultAction.payload.id, ...data }, { fromDefault: true }));
  } else {
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.dealer.questionModels.notification.createFromDefault.failure',
          defaultMessage: "Can't create the question model from the default one.",
        },
      })
    );
  }
}
export default function* QuestionModelsSagas() {
  yield takeLatest(LOCATION_CHANGE, startDetailsApplication);
  yield takeLatest(actionTypes.FETCH_QUESTION_MODELS, fetchQuestionModels);
  yield takeLatest(actionTypes.FETCH_QUESTION_MODEL, fetchQuestionModel);
  yield takeLatest(actionTypes.SELECT_QUESTION_MODEL, afterSelectOne);
  yield takeLatest(actionTypes.UPDATE_QUESTION_MODEL, update);
  yield takeLatest(actionTypes.CREATE_QUESTION_MODEL, create);
  yield takeLatest(actionTypes.DELETE_QUESTION_MODEL, deleteQuestionModel);
  yield takeLatest(actionTypes.CLONE_QUESTION_MODEL, clone);
  yield takeLatest(actionTypes.QUESTION_MODEL_STATISTICS_COUNT, fetchQuestionModelStatisticCount);
  yield takeLatest(actionTypes.SET_QUESTION_MODEL_STATISTICS_ID, fetchQuestionsModelStatisticAnswers);
  yield takeLatest(actionTypes.ADD_FILTER_QUESTION_MODEL_STATISTIC, fetchQuestionsModelStatisticAnswers);
  yield takeLatest(actionTypes.REMOVE_FILTER_QUESTION_MODEL_STATISTIC, fetchQuestionsModelStatisticAnswers);
  yield takeLatest(actionTypes.REMOVE_ALL_FILTER_QUESTION_MODEL_STATISTIC, fetchQuestionsModelStatisticAnswers);
  yield takeLatest(GlobalActionTypes.SET_DEALER_STATISTICS_PERIOD, fetchQuestionsModelStatisticAnswers);
  yield takeLatest(actionTypes.EXPORT_QUESTION_MODEL_STATISTIC, exportQuestionModel);
  yield takeLatest(actionTypes.FETCH_DEFAULT_QUESTION_MODEL, fetchDefaultQuestionModel);
  yield takeLatest(actionTypes.CREATE_FROM_DEFAULT_QUESTION_MODEL, createFromDefaultQuestionModel);
}
